/**
 * Which trades a lane of the tape shows.
 *
 * A token's tape is mostly noise — a hundred wallets nobody has an opinion
 * about. The two lanes that are not noise are the ones where you already know
 * who the wallet is: one you follow, and the one that launched the thing.
 *
 * This is the whole product in one filter, so it is a plain function with
 * tests rather than a predicate buried in a component: addresses arrive in
 * whatever case the chain gave them, a lane can legitimately be empty, and
 * "the dev" is two different fields that usually but not always agree.
 */

export type Lane = 'all' | 'tracked' | 'dev'

export type TapeTrade = { maker: string }

/** Addresses compare case-blind: the same wallet arrives spelled both ways. */
const key = (address: string) => address.toLowerCase()

export type LaneContext = {
  /** Wallets the account follows, as they came from the tracker. */
  tracked?: Iterable<string>
  /** The deployer, and whoever actually sent the first transaction. */
  dev?: ReadonlyArray<string | undefined>
}

export const laneMembers = ({ tracked, dev }: LaneContext) => ({
  tracked: new Set([...(tracked ?? [])].map((address) => key(address))),
  dev: new Set(
    (dev ?? [])
      .filter((address): address is string => Boolean(address))
      .map((address) => key(address)),
  ),
})

export const isTracked = (
  trade: TapeTrade,
  members: ReturnType<typeof laneMembers>,
): boolean => members.tracked.has(key(trade.maker))

export const laneTrades = <T extends TapeTrade>(
  trades: ReadonlyArray<T>,
  lane: Lane,
  members: ReturnType<typeof laneMembers>,
): ReadonlyArray<T> => {
  if (lane === 'all') {
    return trades
  }
  const wanted = lane === 'tracked' ? members.tracked : members.dev
  return trades.filter((trade) => wanted.has(key(trade.maker)))
}

/**
 * What the lane's tab says beside its name.
 *
 * A count, except when it is zero and the lane is one we cannot fill — a tab
 * reading `0` because nothing is followed is a different thing from a tab
 * reading `0` because this dev has not traded, and only the first one is worth
 * sending somebody to the tracker over.
 */
export const laneCount = (
  count: number,
  lane: Lane,
  members: ReturnType<typeof laneMembers>,
): string | undefined => {
  if (lane === 'all') {
    return undefined
  }
  if (lane === 'tracked' && members.tracked.size === 0) {
    return undefined
  }
  return String(count)
}
